import { useEffect, useMemo, useState } from 'react';
import CollectionPage from '~/components/module/setup/CollectionPage';
import { fullName, str } from '~/components/module/setup/cells';
import { getStaffRoleOptions, resetStaffPassword, staffUserAdapter } from '~/apis/staff';
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { IFormField } from '~/utils/interface/setup';

// Internal staff who sign in to this website; their role decides which admin pages they can open.
const Users = () => {
  // undefined until loaded, so the "no roles yet" hint doesn't flash while they are still being fetched
  const [loadedRoles, setLoadedRoles] = useState<{ value: string; label: string }[]>();
  const roles = loadedRoles ?? [];

  useEffect(() => {
    getStaffRoleOptions()
      .then(setLoadedRoles)
      .catch(() => setLoadedRoles([]));
  }, []);

  const fields = useMemo<IFormField[]>(
    () => [
      { name: 'username', labelKey: 'pages.setup.fields.username', type: 'text', required: true, lockedOnEdit: true },
      { name: 'email', labelKey: 'pages.setup.fields.email', type: 'email', required: true, lockedOnEdit: true },
      { name: 'firstName', labelKey: 'pages.setup.fields.firstName', type: 'text', required: true },
      { name: 'lastName', labelKey: 'pages.setup.fields.lastName', type: 'text', required: true },
      {
        name: 'staffRoleId',
        labelKey: 'pages.users.role',
        type: 'select',
        required: true,
        options: roles,
        // a user needs a role, so with none yet the way to create one is right there
        emptyHint: loadedRoles?.length === 0
          ? { messageKey: 'pages.users.noRoles', linkKey: 'pages.users.createRole', to: adminRouteAbsolute.roles }
          : undefined,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [loadedRoles]
  );

  return (
    <CollectionPage
      i18nPrefix='pages.users'
      adapter={staffUserAdapter}
      fields={fields}
      defaultForm={{}}
      getTitle={(form) => fullName(form) || str(form.username)}
      rowActions={[
        {
          key: 'resetPassword',
          labelKey: 'pages.users.resetPassword',
          confirmKey: 'pages.users.resetPasswordConfirm',
          successKey: 'pages.users.resetPasswordDone',
          run: resetStaffPassword,
        },
      ]}
      columns={[
        { key: 'name', labelKey: 'pages.setup.table.name', render: (form) => <span className='font-medium text-foreground'>{fullName(form) || '-'}</span> },
        { key: 'username', labelKey: 'pages.setup.fields.username', render: (form) => str(form.username) },
        { key: 'email', labelKey: 'pages.setup.fields.email', render: (form) => str(form.email) },
        { key: 'role', labelKey: 'pages.users.role', render: (form) => str(form.staffRoleName) || roles.find((role) => role.value === str(form.staffRoleId))?.label },
      ]}
    />
  );
};

export default Users;
